import { useMutation } from '@apollo/client'
import { Delete } from '@mui/icons-material'
import {
    Box,
    Button,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { UpdateCompanyFunds } from 'recruiter/graphql/companies.gql'
import DrawerComponent from 'shared/components/DrawerComponent'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'

const EditFundInfo = ({ open, onClose, refetch, fundInfo }) => {
    const [funds, setFunds] = useState([...fundInfo])
    const toast = useToast()
    const companyId = useParams().companyId ?? ''
    const addNewFund = () => {
        const newFund = {
            name: '',
            year: 0,
            fundSizeMillions: 0,
        }
        setFunds([...funds, newFund])
    }

    const handleFundChange = (index: number, field, value: number | string) => {
        const updatedFunds = funds.map((fund) =>
            funds.indexOf(fund) === index ? { ...fund, [field]: value } : fund
        )
        setFunds(updatedFunds)
    }

    const removeFund = (index: number) => {
        const updatedFunds = funds.filter(
            (fund) => funds.indexOf(fund) !== index
        )
        setFunds(updatedFunds)
    }

    const handleCancel = () => {
        setFunds([...fundInfo])
        onClose()
    }

    const [updateCompanyFunds] = useMutation(UpdateCompanyFunds, {
        onError: (error) => {
            console.error(error)
            toast({
                title: 'Error!',
                message: 'Something went wrong. Please try again later.',
                severity: 'error',
            })
        },
        onCompleted: () => {
            toast({
                title: 'Success!',
                message: 'Company funds updated successfully',
                severity: 'success',
            })
        },
    })

    const handleSave = async () => {
        const fundInfo = funds.map(({ __typename, ...rest }) => rest)

        await updateCompanyFunds({
            variables: {
                companyId,
                fundInformation: fundInfo,
            },
        })
        refetch()
        onClose()
    }

    const unsavedChangesExist = useMemo(() => {
        return JSON.stringify(funds) !== JSON.stringify(fundInfo)
    }, [funds, fundInfo])

    return (
        <DrawerComponent
            headerTitle="Fund Information"
            open={open}
            hasCancelButton
            onCancel={handleCancel}
            onClose={onClose}
            primaryActionText="Save"
            saveButtonDisabled={!unsavedChangesExist}
            onPrimaryAction={handleSave}
        >
            {/* Add New Fund Button */}
            <Box>
                <Button
                    variant="outlined"
                    onClick={addNewFund}
                    sx={{ marginBottom: getPxAsRem(16) }}
                >
                    ADD NEW FUND
                </Button>
            </Box>

            {/* Funds List */}
            <Box sx={{ overflowY: 'auto', paddingBottom: getPxAsRem(80) }}>
                {funds.map((fund, index) => (
                    <Box key={index} sx={{ marginBottom: getPxAsRem(28) }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: getPxAsRem(12),
                            }}
                        >
                            <Typography variant="h6">
                                Fund {index + 1}
                            </Typography>
                            {/* Delete Fund Button */}
                            <IconButton
                                onClick={() => removeFund(index)}
                                sx={{ float: 'right' }}
                            >
                                <Delete />
                            </IconButton>
                        </Box>

                        {/* Fund Name */}
                        <TextField
                            label="Fund Name"
                            fullWidth
                            value={fund.name}
                            onChange={(e) =>
                                handleFundChange(index, 'name', e.target.value)
                            }
                            sx={{ marginBottom: getPxAsRem(16) }}
                        />

                        {/* Fund Year */}
                        <TextField
                            label="Fund Year"
                            fullWidth
                            value={fund.year}
                            type="number"
                            onChange={(e) =>
                                handleFundChange(
                                    index,
                                    'year',
                                    Number(e.target.value)
                                )
                            }
                            sx={{ marginBottom: getPxAsRem(16) }}
                        />

                        {/* Fund Size */}
                        <TextField
                            label="Fund Size (In Millions)"
                            fullWidth
                            value={fund.fundSizeMillions}
                            type="number"
                            onChange={(e) =>
                                handleFundChange(
                                    index,
                                    'fundSizeMillions',
                                    Number(e.target.value)
                                )
                            }
                            sx={{ marginBottom: getPxAsRem(16) }}
                        />

                        {index < funds.length - 1 && <Divider />}
                    </Box>
                ))}
            </Box>
        </DrawerComponent>
    )
}

export default EditFundInfo
